import { FC, ReactNode, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { mq, BreakpointSizes } from '@taleemabad/dsm/themes';
import { AlertDialog } from '../../../../molecules/dialogs-common/alert-popup';
import { Badge } from '../../../../atoms/badge/badge';
import { Button } from '../../../../atoms/button/button';
import { Icon } from '../../../../atoms/icon/icon';
import { Spacing } from '../../../../atoms/spacing/spacing';
import { Typography } from '../../../../atoms/typography/typography';
import { StyledDiv } from '../../../../common/components/styled-div';
import { CardContainer } from '../../../../molecules/card/card-container';
import { RecordingActionDialogs } from '../../../lesson-plan/shared/recording-action-dialogs';
import { Progress } from '../../../../atoms/progress/progress';
import { LoadingWithText } from '../../../../atoms/loading-with-text/loading-with-text';
import { AudioPlayerV2 } from '../../../../molecules/audio-player-v2/audio-player-v2';
import {
  AudioRecorderStatus,
  COACHING_RECORDING_CONFIG,
} from '@taleemabad/shared';
import { CoachingMetadataCard } from '../../../../molecules/coaching/coaching-metadata-card/coaching-metadata-card';

export type RecordingState = AudioRecorderStatus | 'preview';

export interface ObservationRecordingProps {
  subject?: string;
  version?: string;
  grade?: string;
  bookPage?: string;
  students?: string;
  section?: string;
  topic?: string;
  date?: string;
  recordingState: RecordingState;
  elapsedLabel?: string;
  statusText?: string;
  waveformContent?: ReactNode;
  onRecordStart?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onRestart?: () => void;
  onSubmit?: () => void;

  // Dialog Props
  isPermissionDenied?: boolean;
  onClosePermissionDenied?: () => void;
  isMicrophoneBusy?: boolean;
  onCloseMicrophoneBusy?: () => void;
  isDeviceNotSupported?: boolean;
  showExitDialog?: boolean;
  onCloseExitDialog?: () => void;
  onConfirmExitDialog?: () => void;
  exitTitle?: string;
  exitDescription?: string;
  showLimitDialog?: boolean;
  onCloseLimitDialog?: () => void;
  onConfirmLimitSubmit?: () => void;
  showOfflineDialog?: boolean;
  onCloseOfflineDialog?: () => void;
  onConfirmOfflineDialog?: () => void;

  onRequestPermission?: () => void;
  // Preview mode props
  previewMode?: boolean;
  audioPreviewUrl?: string | null;
  audioPreviewSources?: string[];
  onDiscardRecording?: () => void;
  onResumeExistingRecording?: () => void;
  isSynced?: boolean;
  isUploading?: boolean;
  isFailed?: boolean;
  errorMessage?: string;
  onRetryUpload?: () => void;
  uploadProgress?: number;
  statusStep?: string;
  statusDetails?: string;
  allowDownload?: boolean;
  elapsedTime?: number;
  totalDuration?: number;
}

export const ObservationRecording: FC<ObservationRecordingProps> = ({
  subject,
  version,
  grade,
  bookPage,
  students,
  section,
  topic,
  date,
  recordingState,
  elapsedLabel,
  statusText,
  waveformContent,
  onRecordStart,
  onPause,
  onResume,
  onRestart,
  onSubmit,
  isPermissionDenied,
  onClosePermissionDenied,
  isMicrophoneBusy,
  onCloseMicrophoneBusy,
  isDeviceNotSupported,
  showExitDialog = false,
  onCloseExitDialog,
  onConfirmExitDialog,
  exitTitle,
  exitDescription,
  showLimitDialog = false,
  onCloseLimitDialog,
  onConfirmLimitSubmit,
  showOfflineDialog = false,
  onCloseOfflineDialog,
  onConfirmOfflineDialog,
  onRequestPermission,
  previewMode = false,
  audioPreviewUrl,
  audioPreviewSources,
  onDiscardRecording,
  onResumeExistingRecording,
  onRetryUpload,
  isSynced,
  isUploading = false,
  isFailed = false,
  errorMessage,
  uploadProgress = 0,
  statusStep,
  statusDetails,
  allowDownload = false,
  elapsedTime = 0,
  totalDuration = 0,
}) => {
  const formatMs = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };
  const showIdleCTA = recordingState === 'idle';
  const isPaused = recordingState === 'paused';
  const isRecording = recordingState === 'recording';

  const [showRestartConfirmation, setShowRestartConfirmation] = useState(false);
  const [showReRecordConfirmation, setShowReRecordConfirmation] =
    useState(false);

  return (
    <StyledContainer>
      {previewMode ? (
        <>
          <CoachingMetadataCard
            subject={subject}
            version={version}
            grade={grade}
            bookPage={bookPage}
            students={students}
            section={section}
            topic={topic}
            date={date}
          />
          {!isUploading && (
            <StyledStatusBanner
              flexDirection="row"
              alignItem="center"
              justifyContent="center"
              gap={8}
              padding="lg"
              borderRadius={12}
              marginBottom="lg"
            >
              <Icon
                name={isSynced ? 'check_circle' : isFailed ? 'error' : 'info'}
                color={isSynced ? '#059669' : isFailed ? '#DC2626' : '#475569'}
                size="tiny"
              />
              <Typography variant="textLabelLarge" color="inherit">
                {isSynced
                  ? 'Recording uploaded successfully'
                  : isFailed
                    ? (errorMessage || "We couldn't save your review. Please try again.")
                    : 'Recording not yet submitted'}
              </Typography>
            </StyledStatusBanner>
          )}

            <>
              {audioPreviewUrl ? (
                <Spacing marginBottom="xxl" marginTop="xxl">
                  <AudioPlayerV2
                    src={audioPreviewUrl}
                    sources={audioPreviewSources}
                    totalDurationMs={totalDuration}
                    allowDownload={allowDownload}
                  />
                </Spacing>
              ) : (
                <StyledAudioCard>
                  <Spacing marginBottom="md">
                    <Typography variant="textBodyMedium" color="#334155">
                      Your Recording
                    </Typography>
                  </Spacing>
                  <Typography variant="textBodySmall" color="#94A3B8">
                    Loading audio...
                  </Typography>
                  {totalDuration > 0 && (
                    <Spacing marginTop="xs">
                      <Typography variant="textBodySmall" color="#64748B">
                        Total duration: {formatMs(totalDuration)}
                      </Typography>
                    </Spacing>
                  )}
                </StyledAudioCard>
              )}
            </>


          {!isSynced && (
            <>
              {isUploading ? (
                <StyledUploadCard>
                  <StyledDiv flexDirection="column" alignItem="center" gap={16}>
                    <Progress
                      type="circular"
                      indeterminate={uploadProgress === 0}
                      value={uploadProgress}
                    />
                    <StyledDiv
                      flexDirection="column"
                      alignItem="center"
                      gap={4}
                    >
                      <Typography variant="textTitleMedium">
                        {statusStep || (uploadProgress > 0
                          ? `Saving... ${uploadProgress}%`
                          : 'Submitting your review...')}
                      </Typography>
                       <Typography
                        variant="textBodySmall"
                        color="var(--md-sys-color-on-surface-variant)"
                      >
                        {statusDetails || (uploadProgress > 0
                          ? 'Finishing up...'
                          : 'Uploading classroom photos...')}
                      </Typography>
                      <Typography
                        variant="textLabelSmall"
                        color="var(--md-sys-color-on-surface-variant)"
                        css={{ opacity: 0.75, marginTop: '8px' }}
                      >
                        Safe to minimize. We'll finish even if you leave.
                      </Typography>
                    </StyledDiv>
                    {totalDuration > 0 && (
                      <Typography
                        variant="textLabelMedium"
                        color="var(--md-sys-color-outline)"
                      >
                        Duration: {formatMs(totalDuration)}
                      </Typography>
                    )}
                  </StyledDiv>
                </StyledUploadCard>
              ) : isFailed && !isUploading ? (
                <StyledUploadCard>
                  <StyledDiv flexDirection="column" alignItem="center" gap={16}>
                    <Icon
                      name="error"
                      size="large"
                      color="var(--md-sys-color-error)"
                    />
                    <StyledDiv
                      flexDirection="column"
                      alignItem="center"
                      gap={4}
                    >
                      <Typography variant="textTitleMedium">
                        Upload Failed
                      </Typography>
                      <Typography
                        variant="textBodySmall"
                        color="var(--md-sys-color-on-surface-variant)"
                      >
                        {errorMessage || "Please check your connection and try again"}
                      </Typography>
                    </StyledDiv>
                    {onRetryUpload && (
                      <Button
                        variant="filled"
                        onClick={onRetryUpload}
                        icon="refresh"
                      >
                        Retry Upload
                      </Button>
                    )}
                  </StyledDiv>
                </StyledUploadCard>
              ) : (
                <>
                  {onResumeExistingRecording && (
                    <Spacing marginBottom="lg">
                      <Button
                        variant="outlined"
                        width="100%"
                        onClick={onResumeExistingRecording}
                        icon="mic"
                        iconColor="--md-sys-color-primary"
                      >
                        Resume Recording
                      </Button>
                    </Spacing>
                  )}
                  <Spacing marginBottom="lg">
                    <Button
                      variant="outlined"
                      width="100%"
                      onClick={() => setShowReRecordConfirmation(true)}
                      icon="autorenew"
                      iconColor="--md-sys-color-primary"
                    >
                      Re-record
                    </Button>
                  </Spacing>

                  <Button variant="filled" width="100%" onClick={onSubmit}>
                    Submit Review
                  </Button>
                </>
              )}
            </>
          )}

          <AlertDialog
            showPopup={showReRecordConfirmation}
            title="Re-record?"
            description="Your current recording will be deleted. This action cannot be undone."
            primaryButtonText="Re-record"
            secondaryButton={true}
            secondaryButtonText="Cancel"
            onPrimaryButtonClick={() => {
              setShowReRecordConfirmation(false);
              onDiscardRecording?.();
            }}
            onSecondaryButtonClick={() => setShowReRecordConfirmation(false)}
            onClosePopup={() => setShowReRecordConfirmation(false)}
          />
        </>
      ) : (
        <>
          <CoachingMetadataCard
            subject={subject}
            version={version}
            grade={grade}
            bookPage={bookPage}
            students={students}
            section={section}
            topic={topic}
            date={date}
          />

          {showIdleCTA && (
            <StyledInfoBanner>
              <Icon name="info" color="--md-sys-color-primary" size="tiny" />
              <Typography
                variant="textBodySmall"
                color="var(--md-sys-color-on-surface-variant)"
              >
                {`Only ${COACHING_RECORDING_CONFIG.MIN_DURATION_MS / 60000}–${COACHING_RECORDING_CONFIG.MAX_DURATION_MS / 60000} min recordings can be submitted.`}
              </Typography>
            </StyledInfoBanner>
          )}

          <StyledRecordingUIWrapper>
            <StyledWaveformArea>{waveformContent}</StyledWaveformArea>

            <StyledTimerGroup>
              <StyledDiv alignItem="center" gap={12}>
                <StyledRecordDot isRecording={isRecording} />
                <StyledTimerText variant="textTitleLargeOnSurface">
                  {elapsedLabel || '00:00.0'}
                </StyledTimerText>
              </StyledDiv>
              <Typography variant="textBodyMediumOnSurfaceVariant">
                {showIdleCTA ? 'Start recording' : statusText}
              </Typography>
            </StyledTimerGroup>

            <StyledActionsRow>
              <StyledActionIconButton
                onClick={() => setShowRestartConfirmation(true)}
                disabled={showIdleCTA && elapsedTime === 0}
              >
                <Icon
                  name="autorenew"
                  color={
                    showIdleCTA
                      ? '--md-sys-color-outline'
                      : '--md-sys-color-primary'
                  }
                  size="regular"
                />
                <StyledActionLabel
                  variant="textLabelMedium"
                  disabled={showIdleCTA}
                >
                  Re-start
                </StyledActionLabel>
              </StyledActionIconButton>

              <StyledMainActionWrapper>
                <StyledRecordButton
                  onClick={
                    showIdleCTA ? onRecordStart : isPaused ? onResume : onPause
                  }
                  isRecording={isRecording}
                  isPaused={isPaused}
                  data-cy={showIdleCTA ? 'record-start' : 'record-toggle'}
                >
                  <Icon
                    name={
                      showIdleCTA ? 'mic' : isPaused ? 'play_arrow' : 'pause'
                    }
                    color="#FFFFFF"
                    size="regular"
                  />
                  <span>
                    {showIdleCTA ? 'Start' : isPaused ? 'Resume' : 'Pause'}
                  </span>
                </StyledRecordButton>
              </StyledMainActionWrapper>

              <StyledActionIconButton onClick={onSubmit} disabled={showIdleCTA}>
                <Icon
                  name="done"
                  color={
                    showIdleCTA
                      ? '--md-sys-color-outline'
                      : '--md-sys-color-primary'
                  }
                  size="regular"
                />
                <StyledActionLabel
                  variant="textLabelMedium"
                  disabled={showIdleCTA}
                >
                  Submit
                </StyledActionLabel>
              </StyledActionIconButton>
            </StyledActionsRow>
          </StyledRecordingUIWrapper>

          <RecordingActionDialogs
            // Existing ones
            showRestartPopup={showRestartConfirmation}
            onCloseRestartPopup={() => setShowRestartConfirmation(false)}
            onConfirmRestart={() => {
              onRestart?.();
              setShowRestartConfirmation(false);
            }}
            showExitPopup={showExitDialog}
            onCloseExitPopup={onCloseExitDialog}
            onConfirmExit={onConfirmExitDialog}
            exitTitle={exitTitle}
            exitDescription={exitDescription}
            showPermissionDeniedPopup={isPermissionDenied}
            onClosePermissionDeniedPopup={onClosePermissionDenied}
            showMicrophoneBusyPopup={isMicrophoneBusy}
            onCloseMicrophoneBusyPopup={onCloseMicrophoneBusy}
            showOfflinePopup={showOfflineDialog}
            onConfirmOffline={onConfirmOfflineDialog}
            onCloseOffline={onCloseOfflineDialog}
          />
        </>
      )}

      {/* Limit dialog rendered outside conditional so it shows in both preview and recording modes */}
      <RecordingActionDialogs
        showLimitPopup={showLimitDialog}
        onCloseLimitPopup={onCloseLimitDialog}
        onConfirmLimitSubmit={() => {
          onConfirmLimitSubmit?.();
        }}
        submitButtonText="Submit Review"
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const StyledRecordingUIWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: var(--rk-spacing-xl);
  justify-content: space-between;
  padding-bottom: var(--rk-spacing-xl);
`;

const StyledTimerGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--rk-spacing-xs);
`;

const StyledTimerText = styled(Typography)`
  font-size: 72px;
  font-weight: 500;
  color: var(--md-sys-color-on-surface);
  font-variant-numeric: tabular-nums;
  line-height: 1;

  ${mq(BreakpointSizes.desktop)} {
    font-size: 80px;
  }
`;

const StyledMainActionWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 120px;

  ${mq(BreakpointSizes.desktop)} {
    min-width: 180px;
  }
`;

const StyledActionIconButton = styled.button<{ disabled?: boolean }>`
  background: transparent;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  gap: 4px;
  opacity: ${(props) => (props.disabled ? 0.38 : 1)};
  padding: var(--rk-spacing-sm);
  transition: opacity 0.2s;
  min-width: 80px;

  &:active {
    opacity: ${(props) => (props.disabled ? 0.38 : 0.7)};
  }
`;

const StyledActionLabel = styled(Typography)<{ disabled?: boolean }>`
  color: ${(props) =>
    props.disabled
      ? 'var(--md-sys-color-outline)'
      : 'var(--md-sys-color-primary)'};
`;

const StyledWaveformArea = styled.div`
  width: 100%;
  flex: 1;
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--rk-spacing-md);
`;

const StyledRecordButton = styled.button<{
  isRecording?: boolean;
  isPaused?: boolean;
}>`
  min-width: 140px;
  height: 64px;
  flex-shrink: 0;
  border-radius: 64px;
  border: none;
  background-color: ${(props) =>
    props.isRecording
      ? 'var(--md-sys-color-error)'
      : props.isPaused
        ? 'var(--md-sys-color-primary)'
        : 'var(--md-sys-color-error)'};
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 0 var(--rk-spacing-lg);
  cursor: pointer;
  transition: opacity 0.2s ease;
  text-transform: capitalize;
  outline: none;
  -webkit-tap-highlight-color: transparent;

  &:active {
    opacity: 0.8;
    transform: scale(0.98);
  }

  ${mq(BreakpointSizes.desktop)} {
    min-width: 200px;
    height: 80px;
    font-size: 22px;
    border-radius: 80px;
  }
`;

const StyledRecordDot = styled.div<{ isRecording?: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: var(--md-sys-color-error);
  ${(props) =>
    props.isRecording &&
    css`
      animation: blink 1s infinite;
    `}

  @keyframes blink {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.4;
    }
    100% {
      opacity: 1;
    }
  }
`;

const StyledActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--rk-spacing-md);
  width: 100%;
  ${mq(BreakpointSizes.desktop)} {
    justify-content: center;
    gap: 80px;
  }
`;

const StyledInfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: var(--rk-spacing-md);
  background-color: var(--md-sys-color-primary-container);
  border-radius: var(--rk-spacing-lg);
  padding: var(--rk-spacing-md) var(--rk-spacing-lg);
  width: 100%;
  box-sizing: border-box;
  margin-bottom: var(--rk-spacing-md);
`;

const StyledStatusBanner = styled(Spacing)<{
  isSynced?: boolean;
  isUploading?: boolean;
  isFailed?: boolean;
  flexDirection?: string;
  alignItem?: string;
  justifyContent?: string;
  gap?: number;
  borderRadius?: number;
}>`
  display: flex;
  flex-direction: ${(props) => props.flexDirection || 'row'};
  align-items: ${(props) => props.alignItem || 'center'};
  justify-content: ${(props) => props.justifyContent || 'flex-start'};
  gap: ${(props) => (props.gap !== undefined ? `${props.gap}px` : '12px')};
  padding: 10px 16px;
  border-radius: ${(props) =>
    props.borderRadius !== undefined ? `${props.borderRadius}px` : '12px'};
  background-color: ${(props) =>
    props.isSynced
      ? '#ECFDF5'
      : props.isUploading
        ? '#EFF6FF'
        : props.isFailed
          ? '#FEF2F2'
          : '#F1F5F9'};
  color: ${(props) =>
    props.isSynced
      ? '#065F46'
      : props.isUploading
        ? '#1E40AF'
        : props.isFailed
          ? '#991B1B'
          : '#475569'};
  border: 1px solid
    ${(props) =>
      props.isSynced
        ? '#A7F3D0'
        : props.isUploading
          ? '#BFDBFE'
          : props.isFailed
            ? '#FCA5A5'
            : '#E2E8F0'};

  & > div:first-of-type,
  & > .material-symbols-rounded {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  p,
  span {
    line-height: 1.2 !important;
  }
`;

const StyledUploadCard = styled.div`
  background-color: var(--md-sys-color-surface-container-low);
  border-radius: 12px;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 16px 0;
  width: 100%;
  box-sizing: border-box;
`;

const StyledAudioCard = styled.div`
  background: #ffffff;
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
`;
